import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { authService } from '@/services/authService'
import WelcomeModal, { WelcomeModalRef } from '@/components/modals/WelcomeModal'
import './LoginPage.css'

// Where to send the user once they are signed in.
//
// The other Synstream sites (dashboard, pipeline editor) have no login form of
// their own: when their session expires they send the user here with
// ?redirect=<the page they were on>. They sit under this host's parent domain
// and read the same session cookie, so returning them is a plain page load.
//
// Anything outside that parent domain is refused. This page is reachable
// without credentials, so forwarding to an arbitrary URL would make it an open
// redirect: a phishing link could wear the console's own address.
function safeRedirectTarget(raw: string | null): string | null {
  if (!raw) return null

  // A path on the console itself. "//host" is a protocol-relative URL, not a path.
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw

  let url: URL
  try {
    url = new URL(raw, window.location.origin)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null

  const here = window.location.hostname
  if (url.hostname === here) return url.toString()

  // console.<site-domain> -> <site-domain>; every Synstream host lives under it.
  const parent = here.split('.').slice(1).join('.')
  // On a bare two-label host the parent would be a public suffix; refuse rather
  // than open the door to every site under it.
  if (parent.split('.').length >= 2 && (url.hostname === parent || url.hostname.endsWith('.' + parent))) {
    return url.toString()
  }

  // Sites that do not share this domain at all -- a pipeline editor published on
  // a nip.io address, for one. They are named one by one, because the rule above
  // cannot vouch for them and an unchecked redirect target is an open redirect.
  if (extraRedirectHosts().includes(url.hostname)) return url.toString()

  return null
}

// VITE_EXTRA_REDIRECT_HOSTS: comma-separated hostnames, no scheme, no port.
function extraRedirectHosts(): string[] {
  return ((import.meta.env.VITE_EXTRA_REDIRECT_HOSTS as string) || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean)
}

// Whether the session cookie will reach this target on its own.
//
// The cookie is set on the console's parent domain, so a site under that domain
// is signed in the moment it loads. A site outside it never receives the cookie
// and needs the token handed over in the URL -- the same handoff the console
// already uses for its pipeline links.
function sharesSessionCookie(target: string): boolean {
  try {
    const host = new URL(target, window.location.origin).hostname
    const parent = window.location.hostname.split('.').slice(1).join('.')
    if (parent.split('.').length < 2) return host === window.location.hostname
    return host === window.location.hostname || host === parent || host.endsWith('.' + parent)
  } catch {
    return false
  }
}

// Hand the session to a site the cookie cannot reach.
function withAccessToken(target: string): string {
  const token = authService.getAccessToken()
  if (!token) return target
  try {
    const url = new URL(target, window.location.origin)
    url.searchParams.set('access_token', token)
    return url.toString()
  } catch {
    return target
  }
}

const languages = [
  { code: 'zh', name: '繁體中文', short: '繁中' },
  { code: 'zh_CN', name: '简体中文', short: '简中' },
  { code: 'en', name: 'English', short: 'EN' },
  { code: 'jp', name: '日本語', short: 'JP' },
  { code: 'ko', name: '한국어', short: 'KO' }
]

export default function LoginPage() {
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [formErrors, setFormErrors] = useState({
    account: '',
    password: ''
  })
  const [totpRequired, setTotpRequired] = useState(false)
  const [tempToken, setTempToken] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [showRecoveryCode, setShowRecoveryCode] = useState(false)
  const [recoveryCode, setRecoveryCode] = useState('')
  const [staticNodes, setStaticNodes] = useState<Array<{ id: number, x: number, y: number, size: number }>>([])
  const [starNodes, setStarNodes] = useState<Array<{ id: number, x: number, y: number, size: number, delay: number }>>([])
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false)
  const languageMenuRef = useRef<HTMLDivElement>(null)
  const welcomeModalRef = useRef<WelcomeModalRef>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t, i18n } = useTranslation('login')
  const { login, verifyTOTPLogin, verifyRecoveryCodeLogin, refreshAuth } = useAuth()

  // Why the previous session ended, when it ended on its own. Signing in again
  // with no explanation reads as the console having thrown the person out.
  const timeoutReason = searchParams.get('reason')
  const timeoutNotice =
    timeoutReason === 'idle'
      ? t('sessionEnded.idle', 'You were signed out after an hour without activity.')
      : timeoutReason === 'expired'
        ? t('sessionEnded.expired', 'Your session expired. Please sign in again.')
        : ''

  // Sign-in succeeded: go back where the user came from, or to the console home.
  const finishLogin = () => {
    const target = safeRedirectTarget(searchParams.get('redirect'))
    if (target && !target.startsWith('/')) {
      // Another host: a full page load. Under this domain the session cookie
      // travels with it; outside it, the token goes in the URL instead.
      //
      // The pipeline editor is the exception: it never reads the cookie in the
      // browser, its session is the token it stores from ?access_token=. It asks
      // with handoff=token, and gets the token even under this domain, or it
      // comes back signed out and sends the user here again.
      const wantsToken = searchParams.get('handoff') === 'token'
      window.location.assign(sharesSessionCookie(target) && !wantsToken ? target : withAccessToken(target))
      return
    }
    navigate(target || '/')
  }

  // Another site sent the user here to sign in, but the console already has a
  // session. Asking for the password again would be pointless, and an account
  // with two-factor authentication would have to find its code a second time,
  // so renew the session and send the user straight back.
  //
  // Renewing, not just checking, because the stored access token may have
  // expired while the refresh token is still good, and the token handed to the
  // other site has to be a live one. When the renewal fails the session is
  // gone and the form below is the way in.
  const [resuming, setResuming] = useState(
    () => !!searchParams.get('redirect') && !!authService.getUser()
  )
  useEffect(() => {
    if (!resuming) return
    let cancelled = false
    refreshAuth()
      .then(() => {
        if (!cancelled) finishLogin()
      })
      .catch(() => {
        if (!cancelled) setResuming(false)
      })
    return () => {
      cancelled = true
    }
    // Runs once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Fixed background nodes
    const fixedNodes = [
      { id: 1, x: 20, y: 5, size: 8 },
      { id: 2, x: 40, y: 8, size: 9 },
      { id: 3, x: 60, y: 8, size: 7 },
      { id: 4, x: 80, y: 5, size: 10 },
      { id: 5, x: 95, y: 20, size: 8 },
      { id: 6, x: 92, y: 40, size: 9 },
      { id: 7, x: 92, y: 60, size: 7 },
      { id: 8, x: 95, y: 80, size: 8 },
      { id: 9, x: 80, y: 95, size: 9 },
      { id: 10, x: 60, y: 92, size: 10 },
      { id: 11, x: 40, y: 92, size: 8 },
      { id: 12, x: 20, y: 95, size: 9 },
      { id: 13, x: 5, y: 80, size: 7 },
      { id: 14, x: 8, y: 60, size: 8 },
      { id: 15, x: 8, y: 40, size: 9 },
      { id: 16, x: 5, y: 20, size: 8 },
    ]

    // Twinkling star nodes
    const starPositions = [
      { id: 101, x: 15, y: 15, size: 4, delay: 0 },
      { id: 102, x: 85, y: 15, size: 5, delay: 2 },
      { id: 103, x: 25, y: 30, size: 3, delay: 4 },
      { id: 104, x: 75, y: 25, size: 4, delay: 6 },
      { id: 105, x: 12, y: 45, size: 5, delay: 1 },
      { id: 106, x: 88, y: 55, size: 3, delay: 3 },
      { id: 107, x: 30, y: 85, size: 4, delay: 5 },
      { id: 108, x: 70, y: 90, size: 5, delay: 7 },
      { id: 109, x: 45, y: 12, size: 3, delay: 8 },
      { id: 110, x: 55, y: 88, size: 4, delay: 2.5 },
      { id: 111, x: 18, y: 75, size: 3, delay: 4.5 },
      { id: 112, x: 82, y: 35, size: 4, delay: 6.5 },
      { id: 113, x: 65, y: 12, size: 3, delay: 1.5 },
      { id: 114, x: 35, y: 95, size: 4, delay: 3.5 },
      { id: 115, x: 90, y: 75, size: 5, delay: 5.5 },
    ]

    setStaticNodes(fixedNodes)
    setStarNodes(starPositions)
  }, [])

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setLanguageMenuOpen(false)
      }
    }

    if (languageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [languageMenuOpen])

  // Open welcome modal on mount
  useEffect(() => {
    welcomeModalRef.current?.open()
  }, [])

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode)
    localStorage.setItem('language', langCode)
    setLanguageMenuOpen(false)
  }

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === i18n.language) || languages[0]
  }

  const handleBlur = () => {
    // Skip validation during login - just check if filled on submit
    return
  }

  const handleFieldChange = (field: 'account' | 'password', value: string) => {
    // Clear error when user starts typing
    setFormErrors(prev => ({ ...prev, [field]: '' }))
    setError('')

    // Update field value
    switch (field) {
      case 'account':
        setAccount(value)
        break
      case 'password':
        setPassword(value)
        break
    }
  }

  const validateForm = (): boolean => {
    const errors = {
      account: '',
      password: ''
    }

    // For login, just check if fields are filled
    if (!account.trim()) {
      errors.account = t('errors.pleaseFillAllFields', 'Please fill all fields')
    }
    if (!password) {
      errors.password = t('errors.pleaseFillAllFields', 'Please fill all fields')
    }

    setFormErrors(errors)
    return !Object.values(errors).some(error => error !== '')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!validateForm()) {
      return
    }

    try {
      // Login
      const response = await login(account, password)

      // Check if TOTP is required
      if ('totp_required' in response && response.totp_required) {
        setTotpRequired(true)
        setTempToken(response.temp_token)
      } else {
        finishLogin()
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      // Check if error message is a translation key
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, errorMessage))
      } else {
        setError(errorMessage)
      }
    }
  }

  const handleTOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!totpCode || totpCode.length !== 6) {
      setError(t('errors.invalidTOTPCode', 'Please enter a 6-digit code'))
      return
    }

    try {
      await verifyTOTPLogin(tempToken, totpCode)
      finishLogin()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, errorMessage))
      } else {
        setError(errorMessage)
      }
    }
  }

  const handleRecoveryCodeVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!recoveryCode || recoveryCode.trim().length === 0) {
      setError(t('errors.invalidRecoveryCode', 'Please enter a valid recovery code'))
      return
    }

    try {
      await verifyRecoveryCodeLogin(tempToken, recoveryCode.trim())
      finishLogin()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, errorMessage))
      } else {
        setError(errorMessage)
      }
    }
  }

  const handleBackToLogin = () => {
    setTotpRequired(false)
    setTempToken('')
    setTotpCode('')
    setRecoveryCode('')
    setShowRecoveryCode(false)
    setPassword('')
    setError('')
  }

  const handleToggleRecoveryCode = () => {
    setShowRecoveryCode(!showRecoveryCode)
    setError('')
    setTotpCode('')
    setRecoveryCode('')
  }

  // A blank page for the moment the session is renewed, rather than a form
  // that is about to disappear.
  if (resuming) return <div className="login-container" />

  return (
    <div className="login-container">
      {/* Language Selector - Top Right */}
      <div className="absolute top-6 right-6 z-50" ref={languageMenuRef}>
        <button
          onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl hover:bg-white/90 hover:border-primary/30 transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
          </svg>
          <span className="text-sm font-medium text-gray-700">{getCurrentLanguage().short}</span>
          <svg className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${languageMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Language Dropdown Menu */}
        {languageMenuOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-md border border-gray-200/50 rounded-xl shadow-2xl overflow-hidden animate-[slideDown_0.2s_ease-out]">
            <div className="py-2">
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full px-4 py-3 flex items-center justify-between hover:bg-primary/5 transition-colors duration-200 ${
                    i18n.language === language.code ? 'bg-primary/10' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">{language.name}</span>
                    <span className="text-xs text-gray-500">{language.code.toUpperCase()}</span>
                  </div>
                  {i18n.language === language.code && (
                    <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Background particle animation */}
      <div className="particles-container">
        {Array.from({ length: 20 }, (_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Static nodes */}
      <div className="static-nodes">
        {staticNodes.map(node => (
          <div
            key={node.id}
            className="data-node"
            style={{
              left: `${node.x}%`,
              top: `${node.y}%`,
              width: `${node.size}px`,
              height: `${node.size}px`,
            }}
          >
            <div className="node-core"></div>
            <div className="node-pulse"></div>
          </div>
        ))}
      </div>

      {/* Twinkling star nodes */}
      <div className="star-nodes">
        {starNodes.map(star => (
          <div
            key={star.id}
            className="star-node"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              '--star-delay': `${star.delay}s`,
            } as React.CSSProperties}
          >
            <div className="star-core"></div>
            <div className="star-twinkle"></div>
          </div>
        ))}
      </div>

      {/* Flowing line animation */}
      <div className="flowing-connections">
        <svg className="connections-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Edge connectors, back layer */}
          <g className="static-connections">
            {/* Point-to-point edge links */}
            <path d="M20,5 Q30,12 40,8" stroke="var(--color-primary-200)" strokeWidth="0.3" fill="none" opacity="0.15"/>
            <path d="M60,8 Q70,12 80,5" stroke="var(--color-secondary-200)" strokeWidth="0.3" fill="none" opacity="0.15"/>
            <path d="M95,20 Q92,30 92,40" stroke="var(--color-primary-200)" strokeWidth="0.3" fill="none" opacity="0.15"/>
            <path d="M92,60 Q92,70 95,80" stroke="var(--color-secondary-200)" strokeWidth="0.3" fill="none" opacity="0.15"/>
            <path d="M80,95 Q70,92 60,92" stroke="var(--color-primary-200)" strokeWidth="0.3" fill="none" opacity="0.15"/>
            <path d="M40,92 Q30,92 20,95" stroke="var(--color-secondary-200)" strokeWidth="0.3" fill="none" opacity="0.15"/>
            <path d="M5,80 Q8,70 8,60" stroke="var(--color-primary-200)" strokeWidth="0.3" fill="none" opacity="0.15"/>
            <path d="M8,40 Q8,30 5,20" stroke="var(--color-secondary-200)" strokeWidth="0.3" fill="none" opacity="0.15"/>

            {/* Middle layer, routed around the form */}
            <path d="M30,20 Q25,25 20,50" stroke="var(--color-primary-200)" strokeWidth="0.25" fill="none" opacity="0.1"/>
            <path d="M70,20 Q75,25 80,50" stroke="var(--color-secondary-200)" strokeWidth="0.25" fill="none" opacity="0.1"/>
            <path d="M70,80 Q75,75 80,50" stroke="var(--color-primary-200)" strokeWidth="0.25" fill="none" opacity="0.1"/>
            <path d="M30,80 Q25,75 20,50" stroke="var(--color-secondary-200)" strokeWidth="0.25" fill="none" opacity="0.1"/>
          </g>

          {/* Flowing segments */}
          <g className="flowing-segments">
            {/* Flowing edge links */}
            <path className="flowing-segment edge-1" d="M20,5 Q30,12 40,8"
                  stroke="var(--color-primary-500)" strokeWidth="0.5" fill="none" strokeLinecap="round"
                  strokeDasharray="3 12" opacity="0.7">
              <animate attributeName="stroke-dashoffset" values="0;-15" dur="2s" repeatCount="indefinite"/>
            </path>

            <path className="flowing-segment edge-2" d="M60,8 Q70,12 80,5"
                  stroke="var(--color-secondary-500)" strokeWidth="0.5" fill="none" strokeLinecap="round"
                  strokeDasharray="3 12" opacity="0.7">
              <animate attributeName="stroke-dashoffset" values="0;-15" dur="2.2s" repeatCount="indefinite" begin="0.4s"/>
            </path>

            <path className="flowing-segment edge-3" d="M95,20 Q92,30 92,40"
                  stroke="var(--color-primary-500)" strokeWidth="0.5" fill="none" strokeLinecap="round"
                  strokeDasharray="3 12" opacity="0.7">
              <animate attributeName="stroke-dashoffset" values="0;-15" dur="1.8s" repeatCount="indefinite" begin="0.6s"/>
            </path>

            <path className="flowing-segment edge-4" d="M92,60 Q92,70 95,80"
                  stroke="var(--color-secondary-500)" strokeWidth="0.5" fill="none" strokeLinecap="round"
                  strokeDasharray="3 12" opacity="0.7">
              <animate attributeName="stroke-dashoffset" values="0;-15" dur="2.1s" repeatCount="indefinite" begin="0.25s"/>
            </path>

            <path className="flowing-segment edge-5" d="M80,95 Q70,92 60,92"
                  stroke="var(--color-primary-500)" strokeWidth="0.5" fill="none" strokeLinecap="round"
                  strokeDasharray="3 12" opacity="0.7">
              <animate attributeName="stroke-dashoffset" values="0;-15" dur="1.7s" repeatCount="indefinite" begin="0.9s"/>
            </path>

            <path className="flowing-segment edge-6" d="M40,92 Q30,92 20,95"
                  stroke="var(--color-secondary-500)" strokeWidth="0.5" fill="none" strokeLinecap="round"
                  strokeDasharray="3 12" opacity="0.7">
              <animate attributeName="stroke-dashoffset" values="0;-15" dur="2.4s" repeatCount="indefinite" begin="1.1s"/>
            </path>

            <path className="flowing-segment edge-7" d="M5,80 Q8,70 8,60"
                  stroke="var(--color-primary-500)" strokeWidth="0.5" fill="none" strokeLinecap="round"
                  strokeDasharray="3 12" opacity="0.7">
              <animate attributeName="stroke-dashoffset" values="0;-15" dur="1.6s" repeatCount="indefinite" begin="0.75s"/>
            </path>

            <path className="flowing-segment edge-8" d="M8,40 Q8,30 5,20"
                  stroke="var(--color-secondary-500)" strokeWidth="0.5" fill="none" strokeLinecap="round"
                  strokeDasharray="3 12" opacity="0.7">
              <animate attributeName="stroke-dashoffset" values="0;-15" dur="2.15s" repeatCount="indefinite" begin="1.4s"/>
            </path>

            {/* Middle layer: faint and quicker */}
            <path className="flowing-segment subtle-1" d="M30,20 Q25,25 20,50"
                  stroke="var(--color-primary-400)" strokeWidth="0.3" fill="none" strokeLinecap="round"
                  strokeDasharray="2 20" opacity="0.3">
              <animate attributeName="stroke-dashoffset" values="0;-22" dur="3s" repeatCount="indefinite" begin="1.5s"/>
            </path>

            <path className="flowing-segment subtle-2" d="M70,20 Q75,25 80,50"
                  stroke="var(--color-secondary-400)" strokeWidth="0.3" fill="none" strokeLinecap="round"
                  strokeDasharray="2 20" opacity="0.3">
              <animate attributeName="stroke-dashoffset" values="0;-22" dur="3.5s" repeatCount="indefinite" begin="0.5s"/>
            </path>

            <path className="flowing-segment subtle-3" d="M70,80 Q75,75 80,50"
                  stroke="var(--color-primary-400)" strokeWidth="0.3" fill="none" strokeLinecap="round"
                  strokeDasharray="2 20" opacity="0.3">
              <animate attributeName="stroke-dashoffset" values="0;-22" dur="2.8s" repeatCount="indefinite" begin="2s"/>
            </path>

            <path className="flowing-segment subtle-4" d="M30,80 Q25,75 20,50"
                  stroke="var(--color-secondary-400)" strokeWidth="0.3" fill="none" strokeLinecap="round"
                  strokeDasharray="2 20" opacity="0.3">
              <animate attributeName="stroke-dashoffset" values="0;-22" dur="3.2s" repeatCount="indefinite" begin="1s"/>
            </path>
          </g>
        </svg>
      </div>

      {/* Main content */}
      <div className="login-content">
        <div className="login-card">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 bg-white/90 backdrop-blur-xl rounded-2xl flex items-center justify-center shadow-lg border border-primary-200/50 transition-all duration-300 hover:scale-110 hover:shadow-primary-300/50 hover:shadow-xl">
              <img src="/icon.png" alt="Logo" className="w-10 h-10 rounded-sm" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center bg-linear-to-r from-primary to-primary-600 bg-clip-text text-transparent mb-2 transition-all duration-300">
            {totpRequired
              ? showRecoveryCode
                ? t('recovery.title', 'Recovery Code')
                : t('totp.title', 'Two-Factor Authentication')
              : t('title', 'Welcome Back')
            }
          </h1>
          <p className="text-center text-gray-600 mb-8 transition-all duration-300">
            {totpRequired
              ? showRecoveryCode
                ? t('recovery.subtitle', 'Enter your recovery code to continue')
                : t('totp.subtitle', 'Enter verification code to continue')
              : t('subtitle', 'Sign in to your account')
            }
          </p>

          {/* The previous session timed out; not an error, just a reason. */}
          {timeoutNotice && !error && (
            <div className="mb-6 p-4 bg-amber-50/80 backdrop-blur-sm border border-amber-300/50 rounded-xl text-amber-800 text-sm animate-[fadeIn_0.3s_ease-in-out]">
              {timeoutNotice}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50/80 backdrop-blur-sm border border-red-300/50 rounded-xl text-red-600 text-sm animate-[fadeIn_0.3s_ease-in-out]">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={totpRequired ? (showRecoveryCode ? handleRecoveryCodeVerify : handleTOTPVerify) : handleSubmit} className="space-y-5">
            {totpRequired ? (
              showRecoveryCode ? (
                /* Recovery Code Input */
                <div className="transition-all duration-300 animate-[slideDown_0.3s_ease-out]">
                  <label htmlFor="recoveryCode" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('recovery.label', 'Recovery Code')}
                  </label>
                  <input
                    id="recoveryCode"
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                    className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border-2 border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary focus:bg-white/80 transition-all duration-300 text-center font-mono"
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    autoFocus
                    required
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {t('recovery.hint', 'Enter one of your backup recovery codes')}
                  </p>
                  <button
                    type="button"
                    onClick={handleToggleRecoveryCode}
                    className="w-full mt-3 text-primary hover:text-primary-600 text-sm font-medium transition-colors"
                  >
                    {t('recovery.backToTOTP', 'Back to Authenticator Code')}
                  </button>
                </div>
              ) : (
                /* TOTP Verification */
                <div className="transition-all duration-300 animate-[slideDown_0.3s_ease-out]">
                  <label htmlFor="totpCode" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('totp.verificationCode', 'Verification Code')}
                  </label>
                  <input
                    id="totpCode"
                    type="text"
                    value={totpCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setTotpCode(value)
                    }}
                    className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border-2 border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary focus:bg-white/80 transition-all duration-300 text-center text-2xl font-mono tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                    required
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {t('totp.hint', 'Enter the 6-digit code from your authenticator app')}
                  </p>
                  <button
                    type="button"
                    onClick={handleToggleRecoveryCode}
                    className="w-full mt-3 text-primary hover:text-primary-600 text-sm font-medium transition-colors"
                  >
                    {t('totp.useRecoveryCode', 'Lost access? Use recovery code')}
                  </button>
                </div>
              )
            ) : (
              /* Regular Login Fields */
              <>
                <div className="transition-all duration-300">
                  <label htmlFor="account" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('account', 'Account')}
                  </label>
                  <input
                    id="account"
                    type="text"
                    value={account}
                    onChange={(e) => handleFieldChange('account', e.target.value)}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-white/80 transition-all duration-300 ${
                      formErrors.account
                        ? 'border-red-500 focus:border-red-500 hover:border-red-600'
                        : 'border-gray-300/50 focus:border-primary hover:border-primary/50'
                    }`}
                    placeholder={t('accountPlaceholder', 'Enter your account')}
                    required
                  />
                  {formErrors.account && (
                    <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {formErrors.account}
                    </p>
                  )}
                </div>

                <div className="transition-all duration-300">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('password', 'Password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => handleFieldChange('password', e.target.value)}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-white/80 transition-all duration-300 ${
                  formErrors.password
                    ? 'border-red-500 focus:border-red-500 hover:border-red-600'
                    : 'border-gray-300/50 focus:border-primary hover:border-primary/50'
                }`}
                placeholder={t('passwordPlaceholder', 'Enter your password')}
                required
              />
              {formErrors.password && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {formErrors.password}
                </p>
              )}

              {/* No self-service reset by design: an administrator issues a
                  temporary password. The line says where to go rather than
                  leaving a dead end. */}
              <p className="mt-2 text-sm text-gray-500">
                {t('forgotPassword', 'Forgot your password? Ask a platform owner to reset it.')}
              </p>
            </div>
              </>
            )}

            {totpRequired && (
              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full py-3 px-4 border-2 border-gray-300 text-gray-700 font-medium rounded-xl transition-all duration-300 hover:bg-gray-50 hover:border-gray-400"
              >
                {t('totp.backToLogin', 'Back to Login')}
              </button>
            )}

            <button
              type="submit"
              className="w-full py-3 px-4 bg-linear-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-primary-300/50 hover:scale-[1.02] active:scale-[0.98]"
            >
              {totpRequired
                ? showRecoveryCode
                  ? t('recovery.verify', 'Verify Recovery Code')
                  : t('totp.verify', 'Verify')
                : t('loginButton', 'Sign In')
              }
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-8">
          {t('footer', 'SynStream Console')}
        </p>
      </div>

      {/* Welcome Modal */}
      <WelcomeModal ref={welcomeModalRef} />
    </div>
  )
}
